import { extend } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { IHandlers } from "@dhx/ts-common/types";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { addHotkeys } from "@dhx/ts-common/Keymanager";
import { View } from "@dhx/ts-common/view";
import { Popup } from "@dhx/ts-popup";
import { Direction, ISlider, SliderEvents, ISliderConfig} from "./types";

function normalizeValue(value, min, max) {
	if (value < min) {
		return min;
	}
	if (value > max) {
		return max;
	}
	return value;
}

function parseValue(value: number[] | number | string, min: number, max: number): number[] {
	let values;
	if (value === undefined) {
		values = [];
	} else if (Array.isArray(value)) {
		values = value;
	} else if (typeof value === "string") {
		values = value.split(",").map(v => parseInt(v, 10));
	} else {
		values = [value];
	}
	values[0] = values[0] === undefined ? min : normalizeValue(values[0], min, max);
	values[1] = values[1] === undefined ? max : normalizeValue(values[1], min, max);
	return values;
}

export class Slider extends View implements ISlider {
	public config: ISliderConfig;
	public events: IEventSystem<SliderEvents>;
	protected _helper: Popup;
	private _offsets: {left: number, top: number};
	private _currentPosition: number;
	private _extraCurrentPosition: number;
	private _length: number;
	private _axis: string;
	private _isExtraActive: boolean;
	private _disabled: boolean;
	private _isMouseMoving: boolean;

	private _handlers: IHandlers;
	private _possibleRange: [number, number];
	private _findNewDirection: number;

	private _mouseIn: boolean;
	private _focusIn: boolean;

	private _hotkeysDestructor: () => void;

	constructor(container: HTMLElement | string, config: ISliderConfig) {
		super(container, extend({
			mode: Direction.horizontal,
			min: 0,
			max: 100,
			step: 1,
			thumbLabel: true,
		}, config));

		this.events = new EventSystem(this);

		this._axis = this.config.mode === Direction.horizontal ? "clientX" : "clientY";

		this._initStartPosition();
		this._initHotkeys();

		const vNode = create({
			render: () => this._draw(),
			hooks: {
				didMount: () => this._calcSliderPosition(),
				didRedraw: () => this._calcSliderPosition()
			}
		});

		this._initHandlers();
		this.mount(container, vNode);
	}
	disable() {
		this._disabled = true;
		this.paint();
	}
	enable() {
		this._disabled = false;
		this.paint();
	}
	focus(extra?:boolean) {
		this.getRootView().refs[extra ? "extraRunner" : "runner"].el.focus();
	}
	getValue() {
		let res;
		if (this.config.range) {
			const a = this._getValue(this._currentPosition);
			const b = this._getValue(this._extraCurrentPosition);
			res =  a < b ? [a, b] : [b, a];
		} else {
			res = [this._getValue(this._currentPosition)];
		}
		return res;
	}
	setValue(value: string | number | number[]) {
		const old = this._getValue(this._currentPosition);
		if (Array.isArray(value) && value.length > 1) {
			const oldExtra = this._getValue(this._extraCurrentPosition);
			this._setValue(value[0], false);
			this.events.fire(SliderEvents.change, [value[0], old, false]);
			this._setValue(value[1], true);
			this.events.fire(SliderEvents.change, [value[1], oldExtra, true]);
		} else {
			value = parseFloat(value as string);
			if (!isNaN(value)) {
				this._setValue(value as number);
				this.events.fire(SliderEvents.change, [value, old, false]);
			} else {
				throw new Error("Wrong value type, for more info check documentation https://docs.dhtmlx.com/suite/slider__api__slider_setvalue_method.html");
			}
		}

		this.paint();
	}
	destructor() {
		this._hotkeysDestructor();
		this.unmount();
	}
	private _calcSliderPosition() {
		const root = this.getRootView();
		if (!root) {
			return;
		}
		const tracker: HTMLElement = root.refs.track.el;
		const rect = tracker.getBoundingClientRect();
		this._offsets = {
			left: rect.left + window.pageXOffset,
			top: rect.top + window.pageYOffset
		};
		this._length = this.config.mode === Direction.horizontal ? rect.width : rect.height;
	}
	private _initHotkeys() {
		const isRunnersInFocus = () => {
			const activeEl = document.activeElement;
			const refs = this.getRootView().refs;
			if (!refs) {
				return false;
			}
			const runner = refs.runner;
			if (runner && runner.el === activeEl) {
				return true;
			}
			if (this.config.range && refs.extraRunner && refs.extraRunner.el === activeEl) {
				return true;
			}
			return false;
		};

		this._hotkeysDestructor = addHotkeys(
			{
				arrowleft: e => {
					if (this.config.mode === Direction.vertical) {
						return;
					}
					e.preventDefault();
					this._move(-this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));
				},
				arrowright: e => {
					if (this.config.mode === Direction.vertical) {
						return;
					}
					e.preventDefault();
					this._move(this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));
				},
				arrowup: e => {
					if (this.config.mode === Direction.horizontal) {
						return;
					}
					e.preventDefault();
					this._move(this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));

				},
				arrowdown: e => {
					if (this.config.mode === Direction.horizontal) {
						return;
					}
					e.preventDefault();
					this._move(-this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));
				}
			},
			isRunnersInFocus
		);
	}

	private _move(value: number, forExtra?: boolean) {
		if (this.config.inverse) {
			value = -value;
		}

		const { max, min } = this.config;
		const oldValue = forExtra ? this._getValue(this._extraCurrentPosition) : this._getValue(this._currentPosition);
		let newValue = oldValue + value;

		this._setValue(oldValue + value, forExtra);
		if (newValue > max || newValue < min) {
			newValue = oldValue;
		}
		this.events.fire(SliderEvents.change, [newValue, oldValue, forExtra]);
		this.paint();
	}

	private _initStartPosition() {
		const {max, min, range} = this.config;
		const [value, extraValue] = parseValue(this.config.value, this.config.min, this.config.max);

		this._currentPosition = (value - min) / (max - min) * 100;
		if (range) {
			this._extraCurrentPosition = (max - extraValue) / (max - min) * 100;
		}
		this._currentPosition = (value - min) / (max - min) * 100;
		if (range) {
			this._extraCurrentPosition = (extraValue - min) / (max - min) * 100;
		}

		if (this._isInverse()) {
			this._currentPosition = 100 - this._currentPosition;
			if (range) {
				this._extraCurrentPosition = 100 - this._extraCurrentPosition;
			}
		}
	}
	private _getValue(value: number): number {
		if (this._isInverse()) {
			value = 100 - value;
		}
		const {min, max, step} = this.config;
		if (value === 100) {
			return max;
		}
		if (value === 0) {
			return min;
		}
		const val = value * (max - min) / 100;
		const remain = val % step;
		const rounder = remain >= step/2 ? step : 0;
		const result = Number(min) + Number(val) - remain + rounder;
		return +result.toFixed(5);
	}
	private _setValue(val: number, forExtra: boolean = false) {
		const {max, min} = this.config;

		if (val > max || val < min) {
			return false;
		}

		const rawValue = (val - min) / (max - min) * 100;
		const newValue = this._isInverse() ? 100 - rawValue : rawValue;

		if (forExtra) {
			this._extraCurrentPosition = newValue;
		} else {
			this._currentPosition = newValue;
		}
	}
	private _initHandlers() {
		const mouseMove = (e: MouseEvent) => {
			e.preventDefault();
			const x = (e[this._axis] - this._getBegining()) / this._length * 100;

			if (this._findNewDirection) {
				if (Math.abs(this._currentPosition - x) < 1) {
					return;
				}
				if (x > this._currentPosition) {
					this._possibleRange = [this._currentPosition, 100];
				} else {
					this._possibleRange = [0, this._currentPosition];
				}
				this._findNewDirection = null;
			}

			if (this._inSide(x)) {
				this._updatePosition(x, this._isExtraActive);
			}

			this.paint();
		};
		const mouseUp = e => {
			this.events.fire(SliderEvents.mouseup, [e]);

			setTimeout(() => {
				this._isMouseMoving = false;
				this.paint();
			}, 4);

			document.removeEventListener("mouseup", mouseUp);
			document.removeEventListener("mousemove", mouseMove);
		};
		if (this.config.help) {
			this._helper = new Popup({css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light"});
			this._helper.attachHTML(this.config.help);
		}

		this._handlers = {
			showHelper: e => {
				e.preventDefault();
				e.stopPropagation();
				this._helper.show(e.target);
			},
			onmousedown: (e: MouseEvent) => {
				if (this._disabled || e.which === 3) {
					return;
				}
				this.events.fire(SliderEvents.mousedown, [e]);

				this._isMouseMoving = true;
				let active;
				if ((e.target as HTMLElement).classList.contains("dhx_slider__thumb--extra")) {
					this._isExtraActive = true;
					active = this._extraCurrentPosition;
				} else {
					this._isExtraActive = false;
					active = this._currentPosition;
				}
				this._findNewDirection = null;

				// define possible range
				if (this.config.range) {
					const [more, less] = this._currentPosition > this._extraCurrentPosition ?
						[this._currentPosition, this._extraCurrentPosition] : [this._extraCurrentPosition, this._currentPosition];
					if (this._currentPosition === this._extraCurrentPosition) {
						this._findNewDirection = active;
						this._possibleRange = [0, 100];
					} else if (active < more) {
						this._possibleRange = [0, more];
					} else {
						this._possibleRange = [less, 100];
					}
				} else {
					this._possibleRange = [0, 100];
				}

				document.addEventListener("mousemove", mouseMove);
				document.addEventListener("mouseup", mouseUp);

			},
			onlabelClick: () => {
				const refs = this.getRootView().refs;
				refs.runner.el.focus();
			},
			onclick: (e: MouseEvent) => {
				if (this._disabled || this._isMouseMoving || e.which === 3) {
					return;
				}

				const x = (e[this._axis] - this._getBegining()) / this._length * 100;

				const refs = this.getRootView().refs;

				if (this.config.range) {
					const dist = Math.abs(this._currentPosition - x);
					const extraDist = Math.abs(this._extraCurrentPosition - x);
					if (dist < extraDist) {
						this._updatePosition(x, false);
						refs.runner.el.focus();
					} else {
						this._updatePosition(x, true);
						refs.extraRunner.el.focus();
					}
				} else {
					this._updatePosition(x, false);
					refs.runner.el.focus();
				}
				this.paint();
			},
			onmouseover: () => {
				this._mouseIn = true;
				this.paint();
			},
			onmouseout: () => {
				this._mouseIn = false;
				this.paint();
			},
			onfocus: () => {
				this._focusIn = true;
				this.paint();
			},
			onblur: () => {
				this._focusIn = false;
				this.paint();
			}
		};
	}

	private _getBegining(): number {
		return this.config.mode === Direction.horizontal ? this._offsets.left - window.pageXOffset : this._offsets.top - window.pageYOffset;
	}
	private _inSide(x: number) {
		const range = this._possibleRange;
		if (x < range[0]) {
			this._updatePosition(range[0], this._isExtraActive);
			return false;
		}
		if (x > range[1]) {
			this._updatePosition(range[1], this._isExtraActive);
			return false;
		}

		return true;
	}

	private _updatePosition(x: number, extra = false) {
		if (x > 100) {
			x = 100;
		}
		if (x < 0) {
			x = 0;
		}

		const {max, min} = this.config;
		const position = extra ? this._extraCurrentPosition : this._currentPosition;

		const oldValue = this._getValue(position);
		const newValue = this._getValue(x);

		if (oldValue === newValue) {
			return;
		}

		const rawValue = (newValue - min) / (max - min) * 100;
		const value = this._isInverse() ? 100 - rawValue : rawValue;

		if (extra) {
			this._extraCurrentPosition = value;
		} else {
			this._currentPosition = value;
		}
		this.events.fire(SliderEvents.change, [newValue, oldValue, extra]);
	}
	private _getRunnerStyle(forExtra: boolean = false) {
		const direction = this.config.mode === Direction.horizontal ? "left" : "top";
		const pos = forExtra ? this._extraCurrentPosition : this._currentPosition;
		return {
			[direction]: pos + "%",
		};
	}
	private _isInverse() {
		return (this.config.inverse && this.config.mode === Direction.horizontal) ||
			(!this.config.inverse && this.config.mode === Direction.vertical);
	}
	private _getRunnerCss(forExtra: boolean = false) {
		return "dhx_slider__thumb" +
			(forExtra ? " dhx_slider__thumb--extra" : "") +
			(this._isMouseMoving && ((forExtra && this._isExtraActive) || (!forExtra && !this._isExtraActive)) ? " dhx_slider__thumb--active" : "") +
			(this._disabled ? " dhx_slider__thumb--disabled" : "") +
			(this._isNullable(forExtra ? this._extraCurrentPosition : this._currentPosition) && !this.config.range ? " dhx_slider__thumb--nullable" : "");
	}
	private _draw() {
		const width = this.config.labelInline && this.config.labelWidth ? this.config.labelWidth : "";
		return  el("div", {
			class: "dhx_slider" +
				" dhx_slider--mode_" + this.config.mode +
				(this.config.label && this.config.labelInline ? " dhx_slider--label-inline" : "") +
				(this.config.hiddenLabel ? " dhx_slider--label_sr" : "") +
				(this.config.tick ? " dhx_slider--ticks" : "") +
				(this.config.majorTick ? " dhx_slider--major-ticks" : "") +
				(this.config.css ? " " + this.config.css : "")
		}, [
			this.config.label ? el("label.dhx_label.dhx_slider__label", {
				style: {minWidth: width, maxWidth: width},
				class: this.config.help ? "dhx_label--with-help" : "",
				onclick: this._handlers.onlabelClick,
			}, this.config.help ? [
				el("span.dhx_label__holder", this.config.label),
				el("span.dhx_label-help.dxi.dxi-help-circle-outline", {
					tabindex: "0",
					role: "button",
					onclick: this._handlers.showHelper
				}),
			] : this.config.label) : null,
			this._drawSlider()
		]);
	}
	private _drawSlider() {
		return el(".dhx_widget.dhx_slider__track-holder",
			// (this.config.mode === Direction.vertical ? ".dhx_slider--vertical" : ".dhx_slider--horizontal"),
			{
				dhx_widget_id: this._uid,
			}, [
				el(".dhx_slider__track", {
				_ref: "track",
				onmouseover: this._handlers.onmouseover,
				onmouseout: this._handlers.onmouseout,
				onclick: this._handlers.onclick
			}, [
				this._getDetector(),
				el("div", {
					_ref: "runner",
					class: this._getRunnerCss(),
					onmousedown: this._handlers.onmousedown,
					onmousemove: this._handlers.onmousemove,
					onfocus: this._handlers.onfocus,
					onblur: this._handlers.onblur,
					style: this._getRunnerStyle(),
					tabindex: 0,
				}),
				this.config.thumbLabel && (this._mouseIn || this._focusIn || this._isMouseMoving) ? this._drawThumbLabel() : null,
				this.config.thumbLabel && this.config.range && (this._mouseIn || this._focusIn || this._isMouseMoving) ? this._drawThumbLabel(true) : null,
				this.config.range ? el("div", {
					_ref: "extraRunner",
					class: this._getRunnerCss(true),
					onmousedown: this._handlers.onmousedown,
					onmousemove: this._handlers.onmousemove,
					onfocus: this._handlers.onfocus,
					onblur: this._handlers.onblur,
					style: this._getRunnerStyle(true),
					tabindex: 0,
				}) : null,
			]),
			this.config.tick ? this._drawTicks() : null
		]);
	}
	private _getDetector() {
		if (this._disabled) {
			return el(".dhx_slider__range");
		}

		const direction = this.config.mode === Direction.horizontal ? "left" : "top";
		const size = this.config.mode === Direction.horizontal ? "width" : "height";
		if (this.config.range) {
			const [more, less] = this._currentPosition > this._extraCurrentPosition ?
				[this._currentPosition, this._extraCurrentPosition] : [this._extraCurrentPosition, this._currentPosition];
			return el(".dhx_slider__range", {
				style: {
					[direction]: less + "%",
					[size]: more - less + "%"
				}
			});
		}
		if (this._isInverse()) {
			return el(".dhx_slider__range", {
				style: {
					[direction]: this._currentPosition + "%",
					[size]: 100 - this._currentPosition + "%"
				}
			});
		}
		return el(".dhx_slider__range", {
			style: {
				[direction]: 0,
				[size]: this._currentPosition + "%"
			}
		});
	}
	private _drawThumbLabel(forExtra: boolean = false) {
		const pos = forExtra ? this._extraCurrentPosition : this._currentPosition;
		const direction = this.config.mode === Direction.horizontal ? "left" : "top";
		let classNameModifiers = this.config.mode === Direction.horizontal ? ".dhx_slider__thumb-label--horizontal" : ".dhx_slider__thumb-label--vertical";
		if ((forExtra && this._isExtraActive) || (!forExtra && !this._isExtraActive)) {
			classNameModifiers += ".dhx_slider__thumb-label--active";
		}
		const style = {
			[direction]: pos + "%"
		};

		return el(".dhx_slider__thumb-label" + classNameModifiers, {
			style
		}, this._getValue(pos));
	}
	private _getTicks() {
		const {max, min, step, tick, majorTick} = this.config;
		const len = max - min;
		const tickLength = (step * tick) / len;
		const positions = [];
		let length = 0;
		let index = 0;

		while(length < 1) {
			const tickValue = +(Number(min) + length * len).toFixed(5);
			const isMultiple = index % majorTick === 0;
			positions.push({
				position: (this._isInverse() ? (1 - length) * 100 : length * 100) + "%",
				isMultiple,
				label: isMultiple && typeof this.config.tickTemplate === "function" ? this.config.tickTemplate(tickValue) : null
			});
			length += tickLength;
			index ++;
		}
		positions.push({
			position: (this._isInverse() ? 0 : 100) + "%",
			isMultiple: true,
			label: typeof this.config.tickTemplate === "function" ?
				this.config.tickTemplate(max) : null
		});
		return positions;
	}
	private _drawTicks() {
		const direction = this.config.mode === Direction.horizontal ? "left" : "top";
		return el(".dhx_slider__ticks-holder",
			this._getTicks().map(tick => el("div", {
				class: "dhx_slider__tick" + (tick.isMultiple ? " dhx_slider__tick--major" : ""),
				style: {
					[direction]: tick.position
				}
			}, tick.label !== undefined ? [
				el(".dhx_slider__tick-label", tick.label)
			] : null)
		));
	}
	private _isNullable(value) {
		if (this._isInverse()) {
			return value === 100;
		} else {
			return value === 0;
		}
	}
}